import { Fragment, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, isFunction } from 'lodash/fp';
import { useDispatch, useSelector, useRunOnce } from 'app/lib/hooks';
import { Button, ButtonSize, ButtonVariant } from 'app/components/core/Button';
import { Select } from 'app/components/core/Select';
import { Field, useFormContext } from 'app/components/core/Form';
import { doListServiceAccounts, selectServiceAccountsData } from 'app/store/jobs';
import { selectUserCanManageJobs } from 'app/store/users';
import * as styles from './ServiceAccountSelectionField.css';

const FIELD_NAME = 'account.serviceAccountUid';

export function ServiceAccountSelectionField({ onEdit }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const serviceAccounts = useSelector(selectServiceAccountsData);
    const isAdmin = useSelector(selectUserCanManageJobs);
    const { getFieldValue, setFieldValue } = useFormContext();
    const value = getFieldValue(FIELD_NAME);

    useRunOnce(() => {
        dispatch(doListServiceAccounts());
    });

    const serviceAccountOptions = useMemo(
        () =>
            Object.entries(serviceAccounts).map(([, { name, uid }]) => ({
                label: name,
                value: uid
            })),
        [serviceAccounts]
    );

    const hasExistingAccounts = serviceAccountOptions.length > 0;

    const account = useMemo(
        () => serviceAccounts?.find((account) => account.uid === value),
        [serviceAccounts, value]
    );
    const serviceAccountName = account?.name;

    useEffect(() => {
        setFieldValue('account.serviceAccountName', serviceAccountName);
    }, [setFieldValue, serviceAccountName]);

    const handleCreate = useCallback(() => {
        onEdit({
            onCreate: (uid) => setFieldValue(FIELD_NAME, uid)
        });
    }, [onEdit, setFieldValue]);

    const handleEdit = useCallback(() => {
        onEdit({ account });
    }, [onEdit, account]);

    return (
        <Fragment>
            {hasExistingAccounts && (
                <Field
                    component={Select}
                    label={t('jobs:dialogs.environment.serviceAccount')}
                    name={FIELD_NAME}
                    options={serviceAccountOptions}
                    placeholder={t('jobs:dialogs.environment.selectExistingAccount')}
                />
            )}
            {isFunction(onEdit) && isAdmin && (
                <div className={styles.buttons}>
                    <Button
                        icon="add"
                        variant={ButtonVariant.SECONDARY_BLUE}
                        size={ButtonSize.SMALL}
                        onClick={handleCreate}>
                        {t('jobs:serviceAccounts.create')}
                    </Button>
                    {hasExistingAccounts && (
                        <Button
                            icon="edit"
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            disabled={isEmpty(value) || isEmpty(account)}
                            onClick={handleEdit}>
                            {t('jobs:serviceAccounts.edit')}
                        </Button>
                    )}
                </div>
            )}
        </Fragment>
    );
}
